import type { FC } from "react"
import { useMemo, useState } from "react"

import type { AdjustmentLaunchFormValues } from "../schemas"
import type { SelectableAdjustmentType } from "../types"
import { toWarehouseOptions } from "../utils"

import { AddPalletsAdjustmentFormDialog } from "./AddPallets/AddPalletsAdjustmentFormDialog"
import { AdjustmentLaunchFormDialog } from "./AdjustmentLaunchFormDialog"
import { CombinePalletsAdjustmentFormDialog } from "./CombinePallets/CombinePalletsAdjustmentFormDialog"
import { LotCorrectionAdjustmentFormDialog } from "./LotCorrection/LotCorrectionAdjustmentFormDialog"
import { QuantityAdjustmentFormDialog } from "./Quantity/QuantityAdjustmentFormDialog"
import { SplitPalletsAdjustmentFormDialog } from "./SplitPallets/SplitPalletsAdjustmentFormDialog"

import { InventoryAdjustmentType, type InventoryByProductItem } from "@/graphql/codegen/graphql"

interface AdjustmentDialogProps {
  productInventory: InventoryByProductItem
  onClose: () => void
}

export const AdjustmentDialog: FC<AdjustmentDialogProps> = ({ productInventory, onClose }) => {
  const [selectedAdjustmentType, setSelectedAdjustmentType] = useState<SelectableAdjustmentType | null>(null)
  const [selectedWarehouseAreaId, setSelectedWarehouseAreaId] = useState("")

  const warehouseOptions = useMemo(() => toWarehouseOptions(productInventory.warehouses), [productInventory.warehouses])

  const handleContinue = ({ warehouseAreaId, adjustmentType }: AdjustmentLaunchFormValues) => {
    setSelectedWarehouseAreaId(warehouseAreaId)
    setSelectedAdjustmentType(adjustmentType)
  }

  if (selectedAdjustmentType === InventoryAdjustmentType.AdjustQuantity) {
    return (
      <QuantityAdjustmentFormDialog
        product={productInventory.product}
        warehouseAreaId={selectedWarehouseAreaId}
        warehouseInventories={productInventory.warehouses}
        warehouseOptions={warehouseOptions}
        onClose={onClose}
        onSuccess={onClose}
      />
    )
  }

  if (selectedAdjustmentType === InventoryAdjustmentType.SplitPallets) {
    return (
      <SplitPalletsAdjustmentFormDialog
        product={productInventory.product}
        warehouseAreaId={selectedWarehouseAreaId}
        warehouseInventories={productInventory.warehouses}
        warehouseOptions={warehouseOptions}
        onClose={onClose}
        onSuccess={onClose}
      />
    )
  }

  if (selectedAdjustmentType === InventoryAdjustmentType.CombinePallets) {
    return (
      <CombinePalletsAdjustmentFormDialog
        product={productInventory.product}
        warehouseAreaId={selectedWarehouseAreaId}
        warehouseInventories={productInventory.warehouses}
        warehouseOptions={warehouseOptions}
        onClose={onClose}
        onSuccess={onClose}
      />
    )
  }

  if (selectedAdjustmentType === InventoryAdjustmentType.LotBatchCorrection) {
    return (
      <LotCorrectionAdjustmentFormDialog
        product={productInventory.product}
        warehouseAreaId={selectedWarehouseAreaId}
        warehouseInventories={productInventory.warehouses}
        warehouseOptions={warehouseOptions}
        onClose={onClose}
        onSuccess={onClose}
      />
    )
  }

  if (selectedAdjustmentType === InventoryAdjustmentType.AddNewPallets) {
    return (
      <AddPalletsAdjustmentFormDialog
        product={productInventory.product}
        warehouseAreaId={selectedWarehouseAreaId}
        warehouseInventories={productInventory.warehouses}
        warehouseOptions={warehouseOptions}
        onClose={onClose}
        onSuccess={onClose}
      />
    )
  }

  return (
    <AdjustmentLaunchFormDialog
      product={productInventory.product}
      warehouseOptions={warehouseOptions}
      onClose={onClose}
      onContinue={handleContinue}
    />
  )
}
